/**
 * The one status palette. Every badge, chip and status pill resolves its
 * colours here so a state reads the same on every screen ("Confirmed" was
 * green in badge.tsx and blue in status-badge.tsx before this map existed).
 *
 * Tones are brand tokens only: teal for good outcomes, amber for waiting,
 * clay-red for problems, info blue for in-flight work, muted for the past.
 * Clay itself stays reserved for actions (see index.css), so no tone uses
 * bg-primary.
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'ink';

// Dark mode: a see-through tint over the teal card left every tone under
// 4.5:1 for the 12px label, so chips sit on a solid nav-dark ground there
// instead (7:1 or better for every tone).
const DARK_GROUND = 'dark:bg-kanfy-nav-dark';

export const statusToneClass: Record<StatusTone, string> = {
  success: `border-success/20 bg-success/10 text-success ${DARK_GROUND} dark:border-success/40`,
  warning: `border-warning/40 bg-warning/20 text-warning-foreground ${DARK_GROUND} dark:border-warning/50 dark:text-kanfy-amber`,
  danger: `border-destructive/20 bg-destructive/10 text-destructive ${DARK_GROUND} dark:border-destructive/50`,
  info: `border-info/20 bg-info/10 text-info ${DARK_GROUND} dark:border-info/40`,
  neutral: `border-border bg-muted text-muted-foreground ${DARK_GROUND}`,
  ink: 'border-transparent bg-kanfy-ink text-kanfy-cream',
};

/** Domain status → tone. Unknown statuses fall back to neutral. */
export const statusTone: Record<string, StatusTone> = {
  approved: 'success',
  verified: 'success',
  completed: 'success',
  confirmed: 'success',
  paid: 'success',
  pending: 'warning',
  awaiting: 'warning',
  processing: 'info',
  // In flight: a booking in progress or waiting on payment.
  active: 'info',
  in_progress: 'info',
  rejected: 'danger',
  failed: 'danger',
  suspended: 'danger',
  disputed: 'danger',
  // Booking chips fold declined and failed payments into 'cancelled', so it
  // reads as a problem rather than a quiet past state.
  cancelled: 'danger',
  expired: 'neutral',
  inactive: 'neutral',
};

export function toneForStatus(status: string): StatusTone {
  return statusTone[status.toLowerCase().replace(/\s+/g, '_')] ?? 'neutral';
}
